/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import { buildApiUrl } from '../config/api';
import '@fortawesome/fontawesome-free/css/all.css';
import '../styles/RecruiterDashboard.css';
import '../styles/EmployerDashboardMobile.css';
import RecruiterCandidateTracker from '../components/RecruiterCandidateTracker';
import EmployerDashboardMobileHeader from '../components/EmployerDashboardMobileHeader';
import InterviewCalendar from '../components/InterviewCalendar';
import UpcomingInterviewsPanel from '../components/UpcomingInterviewsPanel';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import RecruiterSettings from '../components/RecruiterSettings';
import StatCard from '../components/StatCard';
import AksharCoinsWidget from '../components/AksharCoinsWidget';
import ProfessionalMessaging from '../components/ProfessionalMessaging';
import EmployerRecruiterHeader from '../components/EmployerRecruiterHeader';
import LinkedInStyleSelect from '../components/LinkedInStyleSelect';
import EmployerFilterDropdown from '../components/EmployerFilterDropdown';
import { 
  countries,
  currencies,
  industries,
  departments,
  allSkills,
  jobTypes,
  workModes,
  experienceLevels,
  educationLevels,
  benefits,
  salaryTypes,
  salaryPeriods,
  applyMethods,
  visibilityOptions
} from '../data/formOptions';
import { faBriefcase, faUsers, faUserClock, faHandshake, faUser, faBars, faTimes, faEye } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const DASHBOARD_CACHE_KEY = '__akshar_employer_dashboard_cache';
const DASHBOARD_CACHE_TTL_MS = 60 * 1000;

const buildStatsSnapshot = (jobsList = [], jobAppsList = [], internshipAppsList = []) => {
  const jobsSafe = Array.isArray(jobsList) ? jobsList : [];
  const jobApplicationsSafe = Array.isArray(jobAppsList) ? jobAppsList : [];
  const internshipAppsSafe = Array.isArray(internshipAppsList) ? internshipAppsList : [];

  const activeJobs = jobsSafe.length;
  const totalApplications = jobApplicationsSafe.length + internshipAppsSafe.length;
  const allApps = [...jobApplicationsSafe, ...internshipAppsSafe];

  const inInterview = allApps.filter(app => {
    const status = app?.status?.toLowerCase() || '';
    return status.includes('interview');
  }).length;

  const offersExtended = allApps.filter(app => {
    const status = app?.status?.toLowerCase() || '';
    return status.includes('offer') || status.includes('hired');
  }).length;

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  const newJobsThisWeek = jobsSafe.filter(job => {
    const jobDate = new Date(job.created_at || job.createdAt || job.postedDate || job.publishedAt || 0);
    return !Number.isNaN(jobDate.getTime()) && jobDate >= oneWeekAgo;
  }).length;

  const totalJobApplications = jobsSafe.reduce((total, job) => {
    return total + (job.applicationCount || job.applicants?.length || 0);
  }, 0);

  return {
    activeJobs,
    totalApplications: Math.max(totalApplications, totalJobApplications),
    inInterview,
    offersExtended,
    newJobsThisWeek,
    applicationsIncrease: 0,
    interviewsThisWeek: 0,
    offersAccepted: 0
  };
};

const getUniqueDashboardApplications = (apps) => {
  if (!Array.isArray(apps) || apps.length === 0) return [];
  const sorted = [...apps].sort((a, b) => {
    const dateA = new Date(a.applied_at || a.appliedAt || a.created_at || 0);
    const dateB = new Date(b.applied_at || b.appliedAt || b.created_at || 0);
    return dateB - dateA;
  });
  const uniqueMap = new Map();
  for (const app of sorted) {
    const applicantId = String(app.applicant_id || app.userId || '');
    const jobId = String(app.job_id || app.jobId || app.internship_id || app.internshipId || '');
    const key = `${applicantId}-${jobId}`;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, app);
    }
  }
  return Array.from(uniqueMap.values());
};

const getApplicationStatusLabel = (application) => {
  const raw = application.status_display || application.status || 'Pending';
  return String(raw).toLowerCase() === 'interview scheduled' ? 'Scheduled' : raw;
};

const getApplicationStatusClassName = (application) => {
  const status = String(application.status || 'pending')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/_/g, '-');
  return `status-badge status-${status}`;
};

const getJobApplicationCount = (job) => Math.min(
  25,
  job.visibleApplications !== undefined
    ? job.visibleApplications
    : (job.applicationCount || job.applicants?.length || 0)
);

const EMPLOYER_ACTION_MENU_VIEWPORT_PADDING = 10;
const EMPLOYER_ACTION_MENU_WIDTH = 160;

const computeFixedMenuPosition = (triggerRect) => {
  const padding = EMPLOYER_ACTION_MENU_VIEWPORT_PADDING;
  const viewportWidth = window.innerWidth;
  const menuWidth = Math.min(EMPLOYER_ACTION_MENU_WIDTH, viewportWidth - padding * 2);

  let left = triggerRect.left;
  if (left + menuWidth > viewportWidth - padding) {
    left = triggerRect.right - menuWidth;
  }
  left = Math.max(padding, Math.min(left, viewportWidth - menuWidth - padding));

  const estimatedMenuHeight = 96;
  let top = triggerRect.bottom + 6;
  if (top + estimatedMenuHeight > window.innerHeight - padding) {
    top = Math.max(padding, triggerRect.top - estimatedMenuHeight - 6);
  }

  return { left, top, width: menuWidth };
};

const EmployerDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { id: viewUserId } = useParams(); // Get userId from URL if admin is viewing another user's dashboard
  const isAdminView = !!viewUserId && (user?.role === 'admin' || user?.userType === 'admin');
  
  // Safety check: Redirect to correct dashboard if user is not an employer
  useEffect(() => {
    if (user && !isAdminView) {
      const userRole = user.role || user.userType;
      if (userRole === 'recruiter') {
        console.log('[EmployerDashboard] User is recruiter, redirecting to recruiter dashboard');
        navigate('/recruiter-dashboard', { replace: true });
      } else if (userRole !== 'employer') {
        console.log(`[EmployerDashboard] User role is "${userRole}", not employer. Redirecting...`);
        // Redirect based on actual role
        if (userRole === 'jobSeeker') {
          navigate('/jobseeker-dashboard', { replace: true });
        } else if (userRole === 'intern') {
          navigate('/intern-dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
      } else {
        console.log('[EmployerDashboard] User is employer, staying on employer dashboard');
      }
    }
  }, [user, isAdminView, navigate]);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [jobs, setJobs] = useState([]);
  const [internships, setInternships] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [applications, setApplications] = useState([]);
  const [internshipApplications, setInternshipApplications] = useState([]);
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalApplications: 0,
    inInterview: 0,
    offersExtended: 0,
    newJobsThisWeek: 0,
    applicationsIncrease: 0,
    interviewsThisWeek: 0,
    offersAccepted: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [jobFilter, setJobFilter] = useState('all'); // all, active, draft, closed
  const [internshipFilter, setInternshipFilter] = useState('all'); // all, active, draft, closed
  const [dashboardAppView, setDashboardAppView] = useState('jobs'); // 'jobs' or 'internships' for Recent Applications
  const [openInternshipMenu, setOpenInternshipMenu] = useState(null);
  const [activeInternshipForMenu, setActiveInternshipForMenu] = useState(null);
  const [internshipMenuPosition, setInternshipMenuPosition] = useState(null);
  const [openJobMenu, setOpenJobMenu] = useState(null);
  const [activeJobForMenu, setActiveJobForMenu] = useState(null);
  const [jobMenuPosition, setJobMenuPosition] = useState(null);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [profileCompletion, setProfileCompletion] = useState(0);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [isMobileView, setIsMobileView] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSectionNav = (section) => {
    setActiveSection(section);
    if (isMobileView) {
      setShowMobileSidebar(false);
    }
  };

  // Job view/edit modal states
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editJobData, setEditJobData] = useState({});

  // Account approval: used to gate Post Job / Post Internship until Super Admin approves
  const [employerApproved, setEmployerApproved] = useState(null); // null = loading, true/false = known
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showProfileLockModal, setShowProfileLockModal] = useState(false);

  // Fetch current user's approval status (employer dashboard owner). Returns true/false.
  const fetchApprovalStatus = useCallback(async () => {
    if (!user?.userId && !viewUserId) return false;
    const token = localStorage.getItem('token');
    if (!token) return false;
    try {
      const idToFetch = isAdminView && viewUserId ? viewUserId : user?.userId;
      const url = idToFetch
        ? buildApiUrl(`/api/auth/get_user?userId=${idToFetch}`)
        : buildApiUrl('/api/auth/me');
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const approved = data.isApproved === true;
        setEmployerApproved(approved);
        return approved;
      }
      setEmployerApproved(false);
      return false;
    } catch (err) {
      console.warn('EmployerDashboard: could not fetch approval status', err);
      setEmployerApproved(false);
      return false;
    }
  }, [user?.userId, viewUserId, isAdminView]);

  // Initial fetch and refetch when tab becomes visible (e.g. user was approved in another tab)
  useEffect(() => {
    if (!user?.userId && !viewUserId) return;
    fetchApprovalStatus();

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchApprovalStatus();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [user?.userId, viewUserId, fetchApprovalStatus]);

  const MIN_PROFILE_COMPLETION = 75;

  const ensureProfileCompletionUnlocked = async () => {
    // Always re-check profile completion so newly filled profiles unlock immediately
    const latest = await fetchProfileCompletion();
    const current = typeof latest === 'number' ? latest : profileCompletion;
    if ((current || 0) < MIN_PROFILE_COMPLETION) {
      setShowProfileLockModal(true);
      return false;
    }
    return true;
  };

  const handlePostJobClick = async () => {
    const profileOk = await ensureProfileCompletionUnlocked();
    if (!profileOk) return;

    // Always refetch before deciding, so a just-approved account is recognized
    const apiApproved = employerApproved === true ? true : await fetchApprovalStatus();
    // Grandfather: if they have jobs, they've posted before - treat as approved (fixes "under review" bug)
    const grandfathered = jobs.length > 0;
    const approved = apiApproved || grandfathered;
    if (approved) {
      navigate('/post-job');
    } else {
      setShowApprovalModal(true);
    }
  };

  const handlePostInternshipClick = async () => {
    const profileOk = await ensureProfileCompletionUnlocked();
    if (!profileOk) return;

    const apiApproved = employerApproved === true ? true : await fetchApprovalStatus();
    // Grandfather: if they have internships, they've posted before - treat as approved
    const grandfathered = internships.length > 0;
    const approved = apiApproved || grandfathered;
    if (approved) {
      navigate('/post-internship');
    } else {
      setShowApprovalModal(true);
    }
  };

  const hydrateFromCache = useCallback((cache) => {
    if (!cache) return;
    if (Array.isArray(cache.jobs)) setJobs(cache.jobs);
    if (Array.isArray(cache.internships)) setInternships(cache.internships);
    if (Array.isArray(cache.candidates)) setCandidates(cache.candidates);
    if (Array.isArray(cache.applications)) setApplications(cache.applications);
    if (Array.isArray(cache.internshipApplications)) setInternshipApplications(cache.internshipApplications);
    if (cache.stats) setStats(cache.stats);
    if (cache.activeSection) setActiveSection(cache.activeSection);
    if (cache.jobFilter) setJobFilter(cache.jobFilter);
    if (cache.internshipFilter) setInternshipFilter(cache.internshipFilter);
    if (cache.dashboardAppView) setDashboardAppView(cache.dashboardAppView);
    if (typeof cache.profileCompletion === 'number') setProfileCompletion(cache.profileCompletion);
  }, []);

  const cacheDashboardState = useCallback((overrides = {}) => {
    if (typeof window === 'undefined') return;
    const completion = overrides.profileCompletion ?? profileCompletion;
    window[DASHBOARD_CACHE_KEY] = {
      timestamp: Date.now(),
      jobs: overrides.jobs ?? jobs,
      internships: overrides.internships ?? internships,
      candidates: overrides.candidates ?? candidates,
      applications: overrides.applications ?? applications,
      internshipApplications: overrides.internshipApplications ?? internshipApplications,
      stats: overrides.stats ?? stats,
      activeSection: overrides.activeSection ?? activeSection,
      jobFilter: overrides.jobFilter ?? jobFilter,
      internshipFilter: overrides.internshipFilter ?? internshipFilter,
      dashboardAppView: overrides.dashboardAppView ?? dashboardAppView,
      profileCompletion: typeof completion === 'number' ? completion : 0
    };
  }, [jobs, internships, candidates, applications, internshipApplications, stats, activeSection, jobFilter, internshipFilter, dashboardAppView, profileCompletion]);

  const isInternshipItem = (item) => {
    if (!item) return false;
    return Boolean(
      item.duration ||
      item.stipend ||
      (item.jobTitle && !item.job_title && item.companyName)
    );
  };
  
  // Settings update handler
  const handleUserUpdate = (updatedUser) => {
    // This would typically update the user context
    console.log('User updated:', updatedUser);
  };

  const computeMenuPosition = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return computeFixedMenuPosition(rect);
  };

  const handleToggleInternshipMenu = (menuId, internship, event) => {
    event.stopPropagation();
    if (openInternshipMenu === menuId) {
      setOpenInternshipMenu(null);
      setActiveInternshipForMenu(null);
      setInternshipMenuPosition(null);
    } else {
      setOpenInternshipMenu(menuId);
      setActiveInternshipForMenu(internship);
      setInternshipMenuPosition(computeMenuPosition(event));
    }
  };

  const handleInternshipMenuAction = (action) => {
    if (!activeInternshipForMenu) return;

    if (action === 'view') {
      handleViewInternship(activeInternshipForMenu);
    } else if (action === 'edit') {
      handleEditInternship(activeInternshipForMenu);
    }
    setOpenInternshipMenu(null);
    setActiveInternshipForMenu(null);
    setInternshipMenuPosition(null);
  };

  const handleToggleJobMenu = (menuId, job, event) => {
    event.stopPropagation();
    if (openJobMenu === menuId) {
      setOpenJobMenu(null);
      setActiveJobForMenu(null);
      setJobMenuPosition(null);
    } else {
      setOpenJobMenu(menuId);
      setActiveJobForMenu(job);
      setJobMenuPosition(computeMenuPosition(event));
    }
  };

  const handleJobMenuAction = (action) => {
    if (!activeJobForMenu) return;

    if (action === 'view') {
      handleViewJob(activeJobForMenu);
    } else if (action === 'edit') {
      handleEditJob(activeJobForMenu);
    }
    setOpenJobMenu(null);
    setActiveJobForMenu(null);
    setJobMenuPosition(null);
  };

  // Date formatting function - same as candidate tracker
  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
      if (value.$date) return value.$date;
      if (value.seconds) return new Date(value.seconds * 1000).toISOString();
      if (value._seconds) return new Date(value._seconds * 1000).toISOString();
    }
    try {
      const direct = new Date(value);
      if (!Number.isNaN(direct.getTime())) {
        return direct.toISOString();
      }
    } catch (err) {
      console.warn('Unable to normalize date value', value, err);
    }
    return null;
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return 'N/A';
    
    // Handle already formatted dates (MM/DD/YYYY or M/D/YYYY)
    if (typeof dateInput === 'string') {
      // Check for MM/DD/YYYY or M/D/YYYY format
      const dateMatch = dateInput.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (dateMatch) {
        const [, month, day, year] = dateMatch;
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        if (!Number.isNaN(date.getTime())) {
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
        }
      }
    }
    
    const normalized = normalizeDateValue(dateInput);
    if (!normalized) return 'N/A';
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return 'N/A';
    // Format as "Jan 21, 2026" to match candidate tracker - explicit formatting
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  const dashboardRecentApplications = useMemo(() => {
    const source = dashboardAppView === 'jobs' ? applications : internshipApplications;
    return getUniqueDashboardApplications(source).slice(0, 5);
  }, [applications, internshipApplications, dashboardAppView]);

  const fetchProfileCompletion = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return undefined;

      const url = buildApiUrl('/api/user/profile');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) return undefined;

      const profileData = await response.json();
      const completion = profileData?.profileCompletion ?? profileData?.profileStatus?.completionPercentage ?? 0;
      const value = Math.max(0, Math.min(100, Number(completion) || 0));
      setProfileCompletion(value);

      // Update dashboard cache so next load shows correct % without waiting
      try {
        const c = window[DASHBOARD_CACHE_KEY];
        if (c && typeof value === 'number') {
          c.profileCompletion = value;
          c.timestamp = Date.now();
          window[DASHBOARD_CACHE_KEY] = c;
        }
      } catch (e) {
        // ignore
      }
      return value;
    } catch (error) {
      console.error('Error fetching profile completion:', error);
      return undefined;
    }
  }, []);

  useEffect(() => {
    let usedCache = false;
    if (typeof window !== 'undefined') {
      const cache = window[DASHBOARD_CACHE_KEY];
      if (cache && Date.now() - (cache.timestamp || 0) < DASHBOARD_CACHE_TTL_MS) {
        hydrateFromCache(cache);
        setLoading(false);
        usedCache = true;
      }
    }

    // Fetch profile completion immediately so it appears without waiting for dashboard data
    fetchProfileCompletion();
    fetchAllData({ showSpinner: !usedCache });

    const interval = setInterval(() => {
      fetchApplications({ updateCache: true });
    }, 30000);
    return () => clearInterval(interval);
  }, [hydrateFromCache, fetchProfileCompletion, viewUserId, isAdminView]); // Reload when viewUserId changes (for admin view mode)

  const handleViewCandidate = (candidateId, jobId, applicationType = 'job') => {
    if (!candidateId) {
      return;
    }

    const normalizedType = applicationType === 'internship' ? 'internship' : 'job';
    const targetPath =
      normalizedType === 'internship'
        ? `/intern-profile/${candidateId}`
        : `/profile/${candidateId}`;

    navigate(targetPath, { state: { from: '/employer-dashboard', jobId } });
  };

  useEffect(() => {
    if (!jobs.length && !applications.length && !internshipApplications.length) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const statsSnapshot = buildStatsSnapshot(jobs, applications, internshipApplications);
      setStats(statsSnapshot);
      cacheDashboardState({ stats: statsSnapshot });
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [jobs, applications, internshipApplications, cacheDashboardState]);

  useEffect(() => {
    cacheDashboardState();
  }, [jobs, internships, candidates, applications, internshipApplications, activeSection, jobFilter, internshipFilter, dashboardAppView, cacheDashboardState]);

  useEffect(() => {
    const closeMenus = () => {
      setOpenInternshipMenu(null);
      setActiveInternshipForMenu(null);
      setInternshipMenuPosition(null);
      setOpenJobMenu(null);
      setActiveJobForMenu(null);
      setJobMenuPosition(null);
    };
    document.addEventListener('click', closeMenus);
    window.addEventListener('scroll', closeMenus, true);
    window.addEventListener('resize', closeMenus);
    return () => {
      document.removeEventListener('click', closeMenus);
      window.removeEventListener('scroll', closeMenus, true);
      window.removeEventListener('resize', closeMenus);
    };
  }, []);

  const fetchAllData = async ({ showSpinner = true } = {}) => {
    if (showSpinner) {
      setLoading(true);
    }
    try {
      const [jobsData, internshipsData, candidatesData, applicationsData] = await Promise.all([
        fetchJobs(),
        fetchInternships(),
        fetchCandidates(),
        fetchApplications()
      ]);

      const jobApplicationsData = applicationsData?.jobApplications ?? applications;
      const internshipApplicationsData = applicationsData?.internshipApplications ?? internshipApplications;
      const statsSnapshot = buildStatsSnapshot(
        Array.isArray(jobsData) ? jobsData : jobs,
        Array.isArray(jobApplicationsData) ? jobApplicationsData : [],
        Array.isArray(internshipApplicationsData) ? internshipApplicationsData : []
      );
      setStats(statsSnapshot);
      cacheDashboardState({
        jobs: jobsData ?? jobs,
        internships: internshipsData ?? internships,
        candidates: candidatesData ?? candidates,
        applications: jobApplicationsData,
        internshipApplications: internshipApplicationsData,
        stats: statsSnapshot
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (showSpinner) {
        setLoading(false);
      }
    }
  };

  const fetchJobs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        setJobs([]);
        return [];
      }
      
      // Use the recruiter endpoint which filters by current user's ID (works for both recruiters and employers)
      // If admin is viewing another user's dashboard, include userId parameter
      const url = isAdminView && viewUserId 
        ? buildApiUrl(`/api/jobs/recruiter?userId=${viewUserId}`)
        : buildApiUrl('/api/jobs/recruiter');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const jobsArray = Array.isArray(data) ? data : [];
        const transformedJobs = jobsArray.map(job => ({
          ...job,
          jobTitle: job.job_title || job.jobTitle || '',
          companyName: job.company_name || job.companyName || '',
          salary: job.salary_range || job.salary || '',
          salaryRange: job.salary_range || job.salaryRange || '',
          remoteOption: job.remote_option || job.remoteOption || '',
          jobType: job.job_type || job.jobType || '',
          experienceRequired: job.experience_required || job.experienceRequired || '',
          requiredSkills: job.required_skills || job.requiredSkills || [],
          educationRequired: job.education_required || job.educationRequired || '',
          applicationDeadline: job.application_deadline || job.applicationDeadline || '',
          companyWebsite: job.company_website || job.companyWebsite || ''
        }));
        
        // Sort jobs by created_at date (latest first)
        const sortedJobs = transformedJobs.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || a.postedDate || 0);
          const dateB = new Date(b.created_at || b.createdAt || b.postedDate || 0);
          return dateB - dateA; // Descending order (latest first)
        });
        
        setJobs(sortedJobs);
        return sortedJobs;
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch jobs:', response.status, errorData);
        setJobs([]);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching jobs:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
      setJobs([]);
      return [];
    }
  };

  const handleToggleJobStatus = async (job) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        buildApiUrl(`/api/jobs/jobs/${job._id}/toggle-status`),
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(`✅ Job is now ${data.status.toUpperCase()}`);
        fetchJobs();
      } else {
        alert('❌ Failed to update status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('❌ Error updating status');
    }
  };

  const filteredJobPostings = useMemo(() => {
    return jobs.filter((job) => {
      const statusMatch = jobFilter === 'all'
        || (jobFilter === 'active' && (!job.status || job.status === 'active'))
        || (job.status === jobFilter);
      const searchMatch = !searchTerm
        || (job.job_title || job.title || '').toLowerCase().includes(searchTerm.toLowerCase())
        || (job.job_type || job.jobType || job.type || '').toLowerCase().includes(searchTerm.toLowerCase());
      return statusMatch && searchMatch;
    });
  }, [jobs, jobFilter, searchTerm]);

  const jobFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Jobs', count: jobs.length },
    {
      value: 'active',
      label: 'Active',
      count: jobs.filter((job) => !job.status || job.status === 'active').length
    },
    { value: 'draft', label: 'Drafts', count: jobs.filter((job) => job.status === 'draft').length },
    { value: 'closed', label: 'Closed', count: jobs.filter((job) => job.status === 'closed').length }
  ], [jobs]);

  const filteredInternshipPostings = useMemo(() => {
    return internships.filter((internship) => {
      const statusMatch = internshipFilter === 'all'
        || (internshipFilter === 'active' && (!internship.status || internship.status === 'active'))
        || (internship.status === internshipFilter);

      const title = (internship.jobTitle || internship.job_title || internship.title || '');
      const typeOrDuration = (internship.jobType || internship.job_type || internship.duration || '');
      const searchMatch = !searchTerm
        || title.toLowerCase().includes(searchTerm.toLowerCase())
        || typeOrDuration.toLowerCase().includes(searchTerm.toLowerCase());

      return statusMatch && searchMatch;
    });
  }, [internships, internshipFilter, searchTerm]);

  const internshipFilterOptions = useMemo(() => [
    { value: 'all', label: 'All Internships', count: internships.length },
    {
      value: 'active',
      label: 'Active',
      count: internships.filter((internship) => !internship.status || internship.status === 'active').length
    },
    { value: 'draft', label: 'Drafts', count: internships.filter((internship) => internship.status === 'draft').length },
    { value: 'closed', label: 'Closed', count: internships.filter((internship) => internship.status === 'closed').length }
  ], [internships]);

  const getInternshipApplicationCount = (internship) => Math.min(
    25,
    internship.visibleApplications !== undefined
      ? internship.visibleApplications
      : (internship.applicationCount || internship.applicants?.length || 0)
  );

  const handleToggleInternshipStatus = async (internship) => {
    try {
      const token = localStorage.getItem('token');
      const internshipId = internship?._id || internship?.id;
      if (!token || !internshipId) return;

      const response = await fetch(
        buildApiUrl(`/api/recruiters/internships/${internshipId}/toggle-status`),
        {
          method: 'PUT',
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const data = await response.json();
        alert(`✅ Internship is now ${data.status.toUpperCase()}`);
        fetchInternships();
      } else {
        alert('❌ Failed to update status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('❌ Error updating status');
    }
  };

  const fetchInternships = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setInternships([]);
        return [];
      }
      // If admin is viewing another user's dashboard, include userId parameter
      const url = isAdminView && viewUserId 
        ? buildApiUrl(`/api/recruiters/internships?userId=${viewUserId}`)
        : buildApiUrl('/api/recruiters/internships');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const transformedInternships = (data || []).map(job => ({
          ...job,
          jobTitle: job.job_title || job.jobTitle,
          companyName: job.company_name || job.companyName,
          salary: job.salary_range || job.salary,
          salaryRange: job.salary_range || job.salaryRange,
          remoteOption: job.remote_option || job.remoteOption,
          jobType: job.job_type || job.jobType,
          experienceRequired: job.experience_required || job.experienceRequired,
          requiredSkills: job.required_skills || job.requiredSkills,
          educationRequired: job.education_required || job.educationRequired,
          applicationDeadline: job.application_deadline || job.applicationDeadline,
          companyWebsite: job.company_website || job.companyWebsite
        }));
        
        // Sort internships by created_at/postedAt date (latest first)
        const sortedInternships = transformedInternships.sort((a, b) => {
          const dateA = new Date(a.created_at || a.createdAt || a.postedAt || a.postedDate || 0);
          const dateB = new Date(b.created_at || b.createdAt || b.postedAt || b.postedDate || 0);
          return dateB - dateA; // Descending order (latest first)
        });
        
        setInternships(sortedInternships);
        return sortedInternships;
      }
    } catch (error) {
      console.error('Error fetching internships:', error);
      setInternships([]);
    }
    return [];
  };

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem('token');
      // If admin is viewing another user's dashboard, include userId parameter
      const url = isAdminView && viewUserId 
        ? buildApiUrl(`/api/recruiters/candidates?userId=${viewUserId}`)
        : buildApiUrl('/api/recruiters/candidates');
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCandidates(Array.isArray(data) ? data : []);
        return Array.isArray(data) ? data : [];
      } else {
        setCandidates([]);
      }
    } catch (error) {
      console.error('Error fetching candidates:', error);
      setCandidates([]);
    }
    return [];
  };

  const fetchApplications = async ({ updateCache = false } = {}) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        setApplications([]);
        setInternshipApplications([]);
        return { jobApplications: [], internshipApplications: [] };
      }
      
      let applicationsArray = [];
      // Fetch job applications - include userId if admin view
      const applicationsUrl = isAdminView && viewUserId 
        ? buildApiUrl(`/api/recruiters/applications?userId=${viewUserId}`)
        : buildApiUrl('/api/recruiters/applications');
      const response = await fetch(applicationsUrl, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        applicationsArray = Array.isArray(data) ? data : [];
        setApplications(applicationsArray);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Failed to fetch applications:', response.status, errorData);
        setApplications([]);
      }
      
      let internAppsArray = [];
      // Fetch internship applications - include userId if admin view
      try {
        const internApplicationsUrl = isAdminView && viewUserId 
          ? buildApiUrl(`/api/recruiters/internship-applications?userId=${viewUserId}`)
          : buildApiUrl('/api/recruiters/internship-applications');
        const internResponse = await fetch(internApplicationsUrl, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (internResponse.ok) {
          const internData = await internResponse.json();
          internAppsArray = Array.isArray(internData) ? internData : [];
          setInternshipApplications(internAppsArray);
        } else {
          setInternshipApplications([]);
        }
      } catch (internError) {
        console.error('Error fetching internship applications:', internError);
        setInternshipApplications([]);
      }
      
      if (updateCache) {
        const statsSnapshot = buildStatsSnapshot(jobs, applicationsArray, internAppsArray);
        setStats(statsSnapshot);
        cacheDashboardState({
          applications: applicationsArray,
          internshipApplications: internAppsArray,
          stats: statsSnapshot
        });
      }

      return { jobApplications: applicationsArray, internshipApplications: internAppsArray };
    } catch (error) {
      console.error('❌ Error fetching applications:', error);
      if (error.message) {
        console.error('Error message:', error.message);
      }
      setApplications([]);
      setInternshipApplications([]);
      return { jobApplications: [], internshipApplications: [] };
    }
  };

  const handleViewJob = (job) => {
    setSelectedJob(job);
    setIsEditing(false);
    setShowJobModal(true);
  };

  const handleEditJob = (job) => {
    setSelectedJob(job);
    // Populate all fields from PostJob form structure
    setEditJobData({
      // Basic Information
      title: job.job_title || job.title || '',
      jobType: job.job_type || job.type || job.jobType || 'Full-time',
      workMode: job.work_mode || job.workMode || job.remote_option || 'On-site',
      experienceLevel: job.experience_level || job.experienceLevel || job.experience_required || '',
      vacancies: job.vacancies || job.number_of_positions || 1,
      industry: job.industry || job.domain || '',
      department: job.department || job.functional_area || '',
      skills: Array.isArray(job.skills) ? job.skills : (job.required_skills ? (Array.isArray(job.required_skills) ? job.required_skills : job.required_skills.split(',').map(s => s.trim())) : []),
      tools: Array.isArray(job.tools) ? job.tools : (job.tools_technologies ? (Array.isArray(job.tools_technologies) ? job.tools_technologies : job.tools_technologies.split(',').map(t => t.trim())) : []),
      education: job.education_required || job.education || job.education_level || 'Any',
      
      // Job Description
      summary: job.summary || job.description || job.job_summary || '',
      responsibilities: job.responsibilities || job.key_responsibilities || '',
      desiredProfile: job.desired_profile || job.desired_candidate_profile || '',
      companyOverview: job.company_overview || job.company_description || '',
      
      // Salary & Benefits
      salaryType: job.salary_type || 'Negotiable',
      salaryPeriod: job.salary_period || job.salaryPeriod || 'yearly',
      salaryMin: job.salary_min || job.min_salary || (job.salary_range ? job.salary_range.split('-')[0]?.trim() : ''),
      salaryMax: job.salary_max || job.max_salary || (job.salary_range ? job.salary_range.split('-')[1]?.trim() : ''),
      currency: job.currency || 'USD',
      benefits: Array.isArray(job.benefits) ? job.benefits : [],
      perks: Array.isArray(job.perks) ? job.perks : (job.perks ? [job.perks] : []),
      
      // Location
      country: job.country || '',
      state: job.state || job.province || '',
      city: job.city || '',
      officeAddress: job.office_address || job.address || '',
      postalCode: job.postal_code || job.postalCode || '',
      
      // Application Details (normalize to YYYY-MM-DD for date inputs)
      applicationDeadline: (() => {
        const raw = job.application_deadline || job.applicationDeadline || job.deadline || '';
        if (!raw) return '';
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
      })(),
      preferredJoiningDate: (() => {
        const raw = job.preferred_joining_date || job.preferredJoiningDate || job.joining_date || '';
        if (!raw) return '';
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
      })(),
      applyMethod: job.apply_method || job.applyMethod || 'Apply through AksharJobs portal',
      contactEmail: job.contact_email || job.contactEmail || job.hr_email || '',
      hrName: job.hr_name || job.hrName || job.recruiter_name || '',
      visibility: job.visibility || 'public',
      showCompanyName: job.showCompanyName !== false && job.show_company_name !== false,
      showHrName: job.showHrName !== false && job.show_hr_name !== false,
      
      // Legacy fields for backward compatibility
      job_title: job.job_title || job.title || '',
      company_name: job.company_name || job.company || '',
      location: job.location || '',
      job_type: job.job_type || job.type || '',
      required_skills: job.required_skills || '',
      description: job.description || job.summary || ''
    });
    setIsEditing(true);
    setShowJobModal(true);
  };

  const handleSaveJob = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Determine if it's a job or internship based on the selectedJob data
      const isInternship = isInternshipItem(selectedJob);
      
      let endpoint, dataToSend;
      
      if (isInternship) {
        // Update internship
        endpoint = `/api/recruiters/internships/${selectedJob._id}`;
        dataToSend = editJobData;
      } else {
        // Update job - Transform data to match backend expectations (same format as PostJob)
        const jobData = {
          // Basic Information
          title: editJobData.title || editJobData.job_title || '',
          job_type: editJobData.jobType || editJobData.job_type || 'Full-time',
          work_mode: editJobData.workMode || editJobData.work_mode || 'On-site',
          experience_level: editJobData.experienceLevel || editJobData.experience_level || '',
          vacancies: editJobData.vacancies || 1,
          industry: editJobData.industry || '',
          department: editJobData.department || '',
          skills: Array.isArray(editJobData.skills) ? editJobData.skills : [],
          tools: Array.isArray(editJobData.tools) ? editJobData.tools : [],
          education: editJobData.education || 'Any',
          
          // Job Description
          description: editJobData.summary || editJobData.description || '',
          responsibilities: editJobData.responsibilities || '',
          requirements: editJobData.desiredProfile || editJobData.desired_profile || '',
          company_overview: editJobData.companyOverview || editJobData.company_overview || '',
          
          // Salary & Benefits
          salary_type: editJobData.salaryType || 'Negotiable',
          salary_period: editJobData.salaryPeriod || editJobData.salary_period || 'yearly',
          salary_min: editJobData.salaryMin || editJobData.salary_min || '',
          salary_max: editJobData.salaryMax || editJobData.salary_max || '',
          salary_currency: editJobData.currency || 'USD',
          benefits: Array.isArray(editJobData.benefits) ? editJobData.benefits : [],
          perks: Array.isArray(editJobData.perks) ? editJobData.perks : [],
          
          // Location
          country: editJobData.country || '',
          state: editJobData.state || '',
          city: editJobData.city || '',
          location: editJobData.location || (editJobData.city && editJobData.country 
            ? `${editJobData.city}, ${editJobData.state ? editJobData.state + ', ' : ''}${editJobData.country}` 
            : ''),
          office_address: editJobData.officeAddress || editJobData.office_address || '',
          postal_code: editJobData.postalCode || editJobData.postal_code || '',
          
          // Application Details
          application_deadline: editJobData.applicationDeadline || editJobData.application_deadline || '',
          preferred_joining_date: editJobData.preferredJoiningDate || editJobData.preferred_joining_date || '',
          apply_method: editJobData.applyMethod || editJobData.apply_method || 'Apply through AksharJobs portal',
          contact_email: editJobData.contactEmail || editJobData.contact_email || '',
          hr_name: editJobData.hrName || editJobData.hr_name || '',
          visibility: editJobData.visibility || 'public',
          show_company_name: editJobData.showCompanyName !== false,
          showCompanyName: editJobData.showCompanyName !== false,
          show_hr_name: editJobData.showHrName !== false,
          showHrName: editJobData.showHrName !== false,
          
          // Legacy fields for backward compatibility
          job_title: editJobData.title || editJobData.job_title || '',
          company_name: editJobData.company_name || selectedJob.company_name || selectedJob.company || '',
          required_skills: Array.isArray(editJobData.skills) ? editJobData.skills.join(', ') : (editJobData.required_skills || ''),
          salary_range: (editJobData.salaryMin && editJobData.salaryMax) 
            ? `${editJobData.salaryMin} - ${editJobData.salaryMax}` 
            : (editJobData.salary_range || '')
        };
        
        endpoint = `/api/jobs/update_job/${selectedJob._id}`;
        dataToSend = jobData;
      }
      
      const response = await fetch(buildApiUrl(endpoint), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dataToSend)
      });

      if (response.ok) {
        // Refresh both jobs and internships to ensure data is up-to-date
        if (isInternship) {
          await fetchInternships();
        } else {
          await fetchJobs();
        }
        setShowJobModal(false);
        setSelectedJob(null);
        setEditJobData({});
        alert(isInternship ? 'Internship updated successfully!' : 'Job updated successfully!');
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(`Failed to update ${isInternship ? 'internship' : 'job'}. ${errorData.error || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error updating:', error);
      alert('Error updating. Please try again.');
    }
  };

  const handleCloseModal = () => {
    setShowJobModal(false);
    setSelectedJob(null);
    setIsEditing(false);
    setEditJobData({});
  };

  const handleViewInternship = (internship) => {
    setSelectedJob(internship);
    setIsEditing(false);
    setShowJobModal(true);
  };

  const handleEditInternship = (internship) => {
    setSelectedJob(internship);
    setEditJobData({
      title: internship.jobTitle || internship.job_title || internship.title || '',
      company: internship.companyName || internship.company_name || internship.company || '',
      location: internship.location || '',
      type: internship.remoteOption || internship.remote_option || internship.type || '',
      duration: internship.jobType || internship.job_type || internship.duration || '',
      stipend: internship.salary || internship.salaryRange || internship.salary_range || internship.stipend || '',
      domain: internship.industry || internship.domain || '',
      requiredSkills: Array.isArray(internship.requiredSkills || internship.required_skills) ? (internship.requiredSkills || internship.required_skills).join(', ') : (internship.requiredSkills || internship.required_skills || ''),
      description: internship.description || '',
      responsibilities: internship.responsibilities || '',
      requirements: internship.requirements || '',
      benefits: internship.benefits || '',
      applicationDeadline: internship.applicationDeadline || '',
      status: internship.status || 'active',
      showCompanyName: internship.showCompanyName !== false && internship.show_company_name !== false,
      showHrName: internship.showHrName !== false && internship.show_hr_name !== false
    });
    setIsEditing(true);
    setShowJobModal(true);
  };

  if (loading) {
    return (
      <ThemedLoadingSpinner 
        theme="recruiter"
        size="large"
        text="Loading your dashboard..."
        subText="Preparing your recruitment tools"
        showIcon={true}
        fullScreen={true}
      />
    );
  }

  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'post-job': 'Post Job',
      'my-jobs': 'My Jobs',
      'candidate-tracker': 'Candidate Tracker',
      'messages': 'Messages',
      'calendar': 'Interview Calendar',
      'analytics': 'Analytics',
      'settings': 'Settings'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  const isSelectedInternship = isInternshipItem(selectedJob);

  const actionButtonStyle = {
    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
    border: 'none',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '5px',
    fontSize: '12px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    minWidth: 'auto',
    transition: 'all 0.2s ease',
    fontWeight: '500'
  };

  const searchContainerStyle = {
    position: 'relative',
    width: isMobileView ? '100%' : '360px',
    maxWidth: '100%'
  };

  return (
    <>
      {/* Dropdown Override Styles — mobile compact native select fallback removed; custom dropdown used everywhere */}
      
      {isMobileView ? (
        <EmployerDashboardMobileHeader
          isMessagesOpen={activeSection === 'messages'}
          onOpenMessages={() => {
            setShowMobileSidebar(false);
            setActiveSection((prev) => (prev === 'messages' ? 'dashboard' : 'messages'));
          }}
        />
      ) : (
        <EmployerRecruiterHeader userType="employer" />
      )}

      {isAdminView && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '16px 24px',
          margin: '20px auto',
          maxWidth: '1400px',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faUser} style={{ fontSize: '20px' }} />
            <div>
              <strong>Admin View Mode</strong>
              <div style={{ fontSize: '14px', opacity: 0.9, marginTop: '4px' }}>
                Viewing dashboard for user: {viewUserId}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin?view=profile-browser')}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: 'white',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            Back to Profile Browser
          </button>
        </div>
      )}

      <div className="dashboard-container recruiter-dashboard employer-dashboard-container" style={{ marginTop: '0' }}>
        <div
          className={`sidebar-overlay ${showMobileSidebar ? 'show' : ''}`}
          onClick={() => setShowMobileSidebar(false)}
          aria-hidden="true"
        />

        {/* Sidebar */}
        <div
          className={`sidebar${isMobileView ? ' em-mobile-sidebar' : ''}${showMobileSidebar ? ' show sidebar-open' : ''}`}
          id="sidebar"
          style={isMobileView ? {
            background: 'linear-gradient(180deg, #ff6b35 0%, #10b981 50%, #14b8a6 100%)',
            backgroundColor: '#ff6b35',
            backgroundImage: 'linear-gradient(180deg, #ff6b35 0%, #10b981 50%, #14b8a6 100%)',
            color: '#ffffff',
            paddingTop: '10px'
          } : {
            background: 'linear-gradient(180deg, #ff6b35 0%, #10b981 50%, #14b8a6 100%)',
            backgroundColor: '#ff6b35',
            backgroundImage: 'linear-gradient(180deg, #ff6b35 0%, #10b981 50%, #14b8a6 100%)',
            color: '#ffffff',
            position: 'fixed',
            left: '0',
            top: '80px',
            width: '320px',
            height: 'calc(100vh - 80px)',
            zIndex: 1000,
            paddingTop: '20px'
          }}
          onClick={(e) => {
            if (isMobileView && e.target.closest('.nav-item')) {
              setShowMobileSidebar(false);
            }
          }}
        >
        <div className="nav-menu">
          <div className={`nav-item ${activeSection === 'dashboard' ? 'active' : ''}`} 
               onClick={() => handleSectionNav('dashboard')}>
            <i className="fas fa-th-large"></i>
            <span>Dashboard</span>
          </div>
          <div className={`nav-item ${activeSection === 'jobs' ? 'active' : ''}`} 
               onClick={() => handleSectionNav('jobs')}>
            <i className="fas fa-briefcase"></i>
            <span>Job Postings</span>
            {jobs.length > 0 && <span className="badge">{jobs.length}</span>}
          </div>
          <div className={`nav-item ${activeSection === 'internships' ? 'active' : ''}`} onClick={() => handleSectionNav('internships')}>
            <i className="fas fa-user-graduate"></i>
            <span>Internships</span>
            {internships.length > 0 && <span className="badge">{internships.length}</span>}
          </div>
          <div className={`nav-item ${activeSection === 'candidate-tracker' ? 'active' : ''}`} onClick={() => handleSectionNav('candidate-tracker')}>
            <i className="fas fa-chart-line"></i>
            <span>Candidate Tracker</span>
            {applications.length > 0 && <span className="badge">{applications.length}</span>}
          </div>
          {/* TEMPORARILY DISABLED - Messages */}
          {/* <div className={`nav-item ${activeSection === 'messages' ? 'active' : ''}`} onClick={() => setActiveSection('messages')}>
            <i className="fas fa-envelope"></i>
            <span>Messages</span>
            {applications.filter(app => app.status === 'pending').length > 0 && 
              <span className="badge">{applications.filter(app => app.status === 'pending').length}</span>}
          </div> */}
          <div className={`nav-item ${activeSection === 'calendar' ? 'active' : ''}`} onClick={() => handleSectionNav('calendar')}>
            <i className="fas fa-calendar-alt"></i>
            <span>Interview Calendar</span>
          </div>
          {/* TEMPORARILY DISABLED - Analytics */}
          {/* <div className={`nav-item ${activeSection === 'analytics' ? 'active' : ''}`} onClick={() => setActiveSection('analytics')}>
            <i className="fas fa-chart-bar"></i>
            <span>Analytics</span>
          </div> */}
          {/* TEMPORARILY DISABLED - Akshar Coins */}
          {/* <div className="nav-item" onClick={() => navigate('/promo-code#aksharCoins')}>
            <i className="fas fa-coins"></i>
            <span>Akshar Coins</span>
          </div> */}
          <div className={`nav-item ${activeSection === 'settings' ? 'active' : ''}`} onClick={() => handleSectionNav('settings')}>
            <i className="fas fa-cog"></i>
            <span>Settings</span>
          </div>
          <div className="nav-item" onClick={() => { navigate('/employer-my-profile'); if (isMobileView) setShowMobileSidebar(false); }}>
            <i className="fas fa-user-circle"></i>
            <span>My Profile</span>
          </div>
        </div>

        {/* Profile Completion Card */}
        <div className="sidebar-card" style={{ margin: '20px', padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
            Profile Complete
          </div>
          <div style={{ fontSize: '30px', fontWeight: 700, color: '#fff' }}>
            {Math.max(0, Math.min(100, profileCompletion || 0))}%
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">

        {/* Content Area */}
        <div className="content-area">
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <div className="page-section active" id="dashboard" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              {/* Stats Grid - Modern Design */}
              <div className="stats-grid" style={{ marginTop: '0' }}>
                <StatCard
                  title="Active Postings"
                  value={stats.activeJobs}
                  trend={stats.newJobsThisWeek > 0 ? 20 : 0}
                  trendValue={stats.newJobsThisWeek.toString()}
                  trendLabel="new this week"
                  icon={faBriefcase}
                  iconColor="#3b82f6"
                />
                
                <StatCard
                  title="Total Applications"
                  value={stats.totalApplications}
                  trend={stats.applicationsIncrease || 0}
                  trendValue=""
                  trendLabel="from last month"
                  icon={faUsers}
                  iconColor="#10b981"
                />
                
                <StatCard
                  title="In Interview Stage"
                  value={stats.inInterview}
                  trend={stats.interviewsThisWeek > 0 ? 12 : 0}
                  trendValue={stats.interviewsThisWeek.toString()}
                  trendLabel="scheduled this week"
                  icon={faUserClock}
                  iconColor="#8b5cf6"
                />
                
                <StatCard
                  title="Offers Extended"
                  value={stats.offersExtended}
                  trend={stats.offersAccepted > 0 ? 18 : 0}
                  trendValue={stats.offersAccepted.toString()}
                  trendLabel="accepted"
                  icon={faHandshake}
                  iconColor="#f97316"
                />
              </div>

              <div className="dashboard-content">
                <div className="main-content-section">
                  <div className="recent-applications-section recent-applications-card">
                    <p className="recent-applications-subtitle">
                      Latest applications from the past 7 days
                    </p>

                    <div className="dashboard-app-tabs">
                      <button
                        type="button"
                        className={`dashboard-app-tab${dashboardAppView === 'jobs' ? ' dashboard-app-tab--active' : ''}`}
                        onClick={() => setDashboardAppView('jobs')}
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '600',
                          border: 'none',
                          background: dashboardAppView === 'jobs' ? 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)' : 'transparent',
                          color: dashboardAppView === 'jobs' ? 'white' : '#666',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <i className="fas fa-briefcase"></i>
                        Job Applications ({applications.length})
                      </button>
                      <button
                        type="button"
                        className={`dashboard-app-tab${dashboardAppView === 'internships' ? ' dashboard-app-tab--active' : ''}`}
                        onClick={() => setDashboardAppView('internships')}
                        style={{
                          padding: '8px 16px',
                          fontSize: '13px',
                          fontWeight: '600',
                          border: 'none',
                          background: dashboardAppView === 'internships' ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' : 'transparent',
                          color: dashboardAppView === 'internships' ? 'white' : '#666',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <i className="fas fa-user-graduate"></i>
                        Internship Applications ({internshipApplications.length})
                      </button>
                    </div>

                    {dashboardRecentApplications.length > 0 ? (
                      isMobileView ? (
                        <ul className="recent-applications-mobile-list">
                          {dashboardRecentApplications.map((application, index) => (
                            <li key={application._id || index} className="recent-app-mobile-card">
                              <div className="recent-app-mobile-top">
                                <div className="recent-app-mobile-candidate">
                                  <div className="candidate-name">
                                    {application.candidate_name || application.applicant_name || 'Unknown Candidate'}
                                  </div>
                                  <div className="candidate-position">
                                    {application.job_title || application.jobTitle || 'N/A'}
                                  </div>
                                </div>
                                <div className="recent-app-mobile-title-actions">
                                  <span className={getApplicationStatusClassName(application)}>
                                    {getApplicationStatusLabel(application)}
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-secondary btn-sm recent-app-view-btn"
                                    aria-label="View application"
                                    onClick={() => handleViewCandidate(
                                      application.applicant_id || application.userId,
                                      application.job_id || application.internship_id,
                                      dashboardAppView === 'internships' ? 'internship' : 'job'
                                    )}
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </button>
                                </div>
                              </div>
                              <div className="recent-app-mobile-meta">
                                <span className="recent-app-mobile-date">
                                  Applied {formatDate(application.applied_at || application.appliedAt || application.created_at)}
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="table-container recent-applications-table-wrapper">
                          <table className="recent-applications-table" style={{ width: '100%', tableLayout: 'fixed' }}>
                            <thead>
                              <tr>
                                <th style={{ width: '32%' }}>Candidate</th>
                                <th style={{ width: '22%' }}>Position</th>
                                <th style={{ width: '16%' }}>Applied</th>
                                <th style={{ width: '16%' }}>Status</th>
                                <th style={{ width: '14%' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dashboardRecentApplications.map((application, index) => (
                                <tr key={application._id || index}>
                                  <td>
                                    <div className="candidate-info">
                                      <div className="candidate-details">
                                        <div className="candidate-name">
                                          {application.candidate_name || application.applicant_name || 'Unknown Candidate'}
                                        </div>
                                        <div className="candidate-position">
                                          {application.company_name || 'Company not specified'}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="position-cell" style={{ width: '22%' }}>
                                    {application.job_title || application.jobTitle || 'N/A'}
                                  </td>
                                  <td className="date-cell" style={{ width: '16%' }}>
                                    {formatDate(application.applied_at || application.appliedAt || application.created_at)}
                                  </td>
                                  <td>
                                    <span className={getApplicationStatusClassName(application)}>
                                      {getApplicationStatusLabel(application)}
                                    </span>
                                  </td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-secondary btn-sm"
                                      onClick={() => handleViewCandidate(
                                        application.applicant_id || application.userId,
                                        application.job_id || application.internship_id,
                                        dashboardAppView === 'internships' ? 'internship' : 'job'
                                      )}
                                    >
                                      View Details
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )
                    ) : (
                      <div className="recent-applications-empty">
                        <div className="empty-content">
                          <i className={`fas ${dashboardAppView === 'jobs' ? 'fa-briefcase' : 'fa-user-graduate'}`}></i>
                          <p>No {dashboardAppView === 'jobs' ? 'job' : 'internship'} applications yet</p>
                          <small>Applications will appear here when candidates apply to your {dashboardAppView === 'jobs' ? 'jobs' : 'internships'}</small>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              
              </div>
            </div>
          )}


          {/* Job Postings Section */}
          {activeSection === 'jobs' && (
            <div className="page-section active" id="jobs" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              <div className={isMobileView ? 'job-postings-section' : 'card job-postings-card'}>
                <div 
                  className="card-header job-postings-header"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Search Bar */}
                  <div className="job-postings-search" style={searchContainerStyle}>
                    <input
                      type="text"
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 40px 8px 15px',
                        fontSize: '13px',
                        border: '2px solid #FF8A65',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#333',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <i className="fas fa-search" style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#FF8A65',
                      fontSize: '14px',
                      pointerEvents: 'none'
                    }}></i>
                  </div>
                  
                  <div className="job-postings-toolbar">
                    <EmployerFilterDropdown
                      value={jobFilter}
                      onChange={setJobFilter}
                      options={jobFilterOptions}
                      className={isMobileView ? '' : 'employer-filter-dropdown--compact'}
                    />

                    <button
                      className="btn btn-primary"
                      onClick={handlePostJobClick}
                      style={{ 
                        ...actionButtonStyle, 
                        minWidth: '110px',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: '11px' }}></i> Post Job
                    </button>
                  </div>
                </div>

                {isMobileView ? (
                  filteredJobPostings.length > 0 ? (
                    <ul className="employer-jobs-mobile-list">
                      {filteredJobPostings.map((job, index) => {
                        const menuId = job._id || job.id || index;
                        const jobStatus = (job.status || 'active').toLowerCase();
                        return (
                          <li key={menuId} className="employer-job-mobile-card">
                            <div className="employer-job-mobile-top">
                              <div className="employer-job-mobile-leading">
                                <span className="employer-job-mobile-icon" aria-hidden="true">
                                  <i className="fas fa-briefcase" />
                                </span>
                                <div className="employer-job-mobile-heading">
                                  <h4 className="employer-job-mobile-title">
                                    {job.job_title || job.title || 'Untitled Job'}
                                  </h4>
                                  <span className="employer-job-mobile-type">
                                    {job.job_type || job.jobType || job.type || 'Full-time'}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className={`employer-job-mobile-status status-${jobStatus}`}
                                onClick={() => handleToggleJobStatus(job)}
                                title="Tap to toggle Active/Inactive"
                              >
                                {(job.status || 'Active').toUpperCase()}
                              </button>
                            </div>
                            <div className="employer-job-mobile-footer">
                              <div className="employer-job-mobile-metrics">
                                <div className="employer-job-mobile-metric">
                                  <span className="employer-job-mobile-metric-value">
                                    {getJobApplicationCount(job)}
                                  </span>
                                  <span className="employer-job-mobile-metric-label">Applications</span>
                                </div>
                                <div className="employer-job-mobile-metric-divider" aria-hidden="true" />
                                <div className="employer-job-mobile-metric">
                                  <span className="employer-job-mobile-metric-value">
                                    {job.views || 0}
                                  </span>
                                  <span className="employer-job-mobile-metric-label">Views</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="employer-job-mobile-action-btn btn-custom-size"
                                aria-label="Job actions"
                                onClick={(e) => handleToggleJobMenu(menuId, job, e)}
                              >
                                <i className="fas fa-ellipsis-v" aria-hidden="true" />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="employer-jobs-empty">
                      <i className="fas fa-briefcase" aria-hidden="true" />
                      <h3>{jobs.length === 0 ? 'No jobs posted yet' : `No ${jobFilter} jobs`}</h3>
                      <p>
                        {jobs.length === 0
                          ? 'Start by posting your first job to see it here!'
                          : `You don't have any ${jobFilter} jobs at the moment.`}
                      </p>
                    </div>
                  )
                ) : (
                <div className="table-container employer-postings-table-wrapper">
                  <table className="employer-postings-table">
                    <thead>
                      <tr>
                        <th>Job Title</th>
                        <th>Type</th>
                        <th>Applications</th>
                        <th>Views</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredJobPostings.length > 0 ? (
                          filteredJobPostings.map((job, index) => (
                          <tr key={job._id || index}>
                            <td>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <strong style={{ color: '#FF8A65', fontSize: '14px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {job.job_title || job.title || 'Untitled Job'}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <span style={{ 
                                background: '#fff3e0', 
                                color: '#FF8A65', 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {job.job_type || job.jobType || job.type || 'Full-time'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {getJobApplicationCount(job)}
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {job.views || 0}
                            </td>
                            <td>
                              <span 
                                className={`status-badge status-${(job.status || 'active').toLowerCase()}`}
                                style={{
                                  background: (job.status || 'active').toLowerCase() === 'active' ? '#e8f5e8' : '#fee2e2',
                                  color: (job.status || 'active').toLowerCase() === 'active' ? '#2e7d32' : '#991b1b',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  textTransform: 'uppercase'
                                }}
                                onClick={() => handleToggleJobStatus(job)}
                                title="Click to toggle between Active/Inactive"
                              >
                                {(job.status || 'Active').toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  type="button"
                                  className="employer-postings-action-btn"
                                  onClick={(e) => {
                                    const menuId = job._id || job.id || index;
                                    handleToggleJobMenu(menuId, job, e);
                                  }}
                                  style={{
                                    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '6px 14px',
                                    borderRadius: '5px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    minWidth: '110px',
                                    fontWeight: '500',
                                    boxShadow: '0 4px 10px rgba(255, 138, 101, 0.25)'
                                  }}
                                >
                                  <i className="fas fa-ellipsis-v" style={{ fontSize: '12px' }}></i>
                                  Actions
                                  <i className="fas fa-chevron-down" style={{ fontSize: '10px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                        ) : (
                          <tr>
                            <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                              <i className="fas fa-briefcase" style={{ fontSize: '48px', color: '#FF8A65', marginBottom: '16px' }}></i>
                              <h3>{jobs.length === 0 ? 'No jobs posted yet' : `No ${jobFilter} jobs`}</h3>
                              <p>{jobs.length === 0 ? 'Start by posting your first job to see it here!' : `You don't have any ${jobFilter} jobs at the moment.`}</p>
                              {jobs.length === 0 && (
                                <button className="btn btn-primary btn-post-job" onClick={handlePostJobClick} style={{ marginTop: '16px' }}>
                                  <i className="fas fa-plus"></i> Post Your First Job
                                </button>
                              )}
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            </div>
          )}

          {/* Internships Section */}
          {activeSection === 'internships' && (
            <div className="page-section active" id="internships" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              <div className={isMobileView ? 'job-postings-section' : 'card job-postings-card'}>
                <div 
                  className="card-header job-postings-header"
                  style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Search Bar */}
                  <div className="job-postings-search" style={searchContainerStyle}>
                    <input
                      type="text"
                      placeholder="Search internships..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 40px 8px 15px',
                        fontSize: '13px',
                        border: '2px solid #FF8A65',
                        borderRadius: '6px',
                        background: 'white',
                        color: '#333',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    <i className="fas fa-search" style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#FF8A65',
                      fontSize: '14px',
                      pointerEvents: 'none'
                    }}></i>
                  </div>
                  
                  <div className="job-postings-toolbar">
                    <EmployerFilterDropdown
                      value={internshipFilter}
                      onChange={setInternshipFilter}
                      options={internshipFilterOptions}
                      className={isMobileView ? '' : 'employer-filter-dropdown--compact'}
                    />

                    <button 
                      className="btn btn-primary"
                      onClick={handlePostInternshipClick}
                      style={{ 
                        ...actionButtonStyle, 
                        minWidth: '110px',
                        justifyContent: 'center'
                      }}
                    >
                      <i className="fas fa-plus" style={{ fontSize: '11px' }}></i> Post Internship
                    </button>
                  </div>
                </div>

                {isMobileView ? (
                  filteredInternshipPostings.length > 0 ? (
                    <ul className="employer-jobs-mobile-list">
                      {filteredInternshipPostings.map((internship, index) => {
                        const menuId = internship._id || internship.id || index;
                        const internshipStatus = (internship.status || 'active').toLowerCase();
                        return (
                          <li key={menuId} className="employer-job-mobile-card">
                            <div className="employer-job-mobile-top">
                              <div className="employer-job-mobile-leading">
                                <span className="employer-job-mobile-icon" aria-hidden="true">
                                  <i className="fas fa-user-graduate" />
                                </span>
                                <div className="employer-job-mobile-heading">
                                  <h4 className="employer-job-mobile-title">
                                    {internship.jobTitle || internship.job_title || internship.title || 'Untitled Internship'}
                                  </h4>
                                  <span className="employer-job-mobile-type">
                                    {internship.jobType || internship.job_type || internship.duration || '3-6 months'}
                                  </span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className={`employer-job-mobile-status status-${internshipStatus}`}
                                onClick={() => handleToggleInternshipStatus(internship)}
                                title="Tap to toggle Active/Inactive"
                              >
                                {(internship.status || 'Active').toUpperCase()}
                              </button>
                            </div>

                            <div className="employer-job-mobile-footer">
                              <div className="employer-job-mobile-metrics">
                                <div className="employer-job-mobile-metric">
                                  <span className="employer-job-mobile-metric-value">
                                    {getInternshipApplicationCount(internship)}
                                  </span>
                                  <span className="employer-job-mobile-metric-label">Applications</span>
                                </div>
                                <div className="employer-job-mobile-metric-divider" aria-hidden="true" />
                                <div className="employer-job-mobile-metric">
                                  <span className="employer-job-mobile-metric-value">
                                    {internship.views || 0}
                                  </span>
                                  <span className="employer-job-mobile-metric-label">Views</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="employer-job-mobile-action-btn btn-custom-size"
                                aria-label="Internship actions"
                                onClick={(e) => handleToggleInternshipMenu(menuId, internship, e)}
                              >
                                <i className="fas fa-ellipsis-v" aria-hidden="true" />
                              </button>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <div className="employer-jobs-empty">
                      <h3 className="employer-empty-title">
                        <i className="fas fa-graduation-cap" aria-hidden="true" />
                        {internships.length === 0 ? 'No internships posted yet' : `No ${internshipFilter} internships`}
                      </h3>
                      <p>{internships.length === 0 ? 'Start by posting your first internship to see it here!' : `You don't have any ${internshipFilter} internships at the moment.`}</p>
                    </div>
                  )
                ) : (
                  <div className="table-container employer-postings-table-wrapper">
                    <table className="employer-postings-table">
                      <thead>
                        <tr>
                          <th>Internship Title</th>
                          <th>Duration</th>
                          <th>Applications</th>
                          <th>Views</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredInternshipPostings.length > 0 ? (
                          filteredInternshipPostings.map((internship, index) => (
                          <tr key={internship._id || internship.id || index}>
                            <td>
                              <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                <strong style={{ color: '#FF8A65', fontSize: '14px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {internship.jobTitle || internship.job_title || internship.title || 'Untitled Internship'}
                                </strong>
                              </div>
                            </td>
                            <td>
                              <span style={{ 
                                background: '#fff3e0', 
                                color: '#FF8A65', 
                                padding: '4px 8px', 
                                borderRadius: '4px', 
                                fontSize: '12px',
                                fontWeight: '500'
                              }}>
                                {internship.jobType || internship.job_type || internship.duration || '3-6 months'}
                              </span>
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {getInternshipApplicationCount(internship)}
                            </td>
                            <td style={{ textAlign: 'center', fontWeight: 600 }}>
                              {internship.views || 0}
                            </td>
                            <td>
                              <span 
                                className={`status-badge status-${(internship.status || 'active').toLowerCase()}`} 
                                style={{
                                  background: (internship.status || 'active').toLowerCase() === 'active' ? '#e8f5e8' : '#fee2e2',
                                  color: (internship.status || 'active').toLowerCase() === 'active' ? '#2e7d32' : '#991b1b',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  textTransform: 'uppercase'
                                }}
                                onClick={() => handleToggleInternshipStatus(internship)}
                                title="Click to toggle between Active/Inactive"
                              >
                                {(internship.status || 'Active').toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <button
                                  type="button"
                                  className="employer-postings-action-btn"
                                  onClick={(e) => {
                                    const menuId = internship._id || internship.id || index;
                                    handleToggleInternshipMenu(menuId, internship, e);
                                  }}
                                  style={{
                                    background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                                    border: 'none',
                                    color: 'white',
                                    padding: '6px 14px',
                                    borderRadius: '5px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    minWidth: '110px',
                                    fontWeight: '500',
                                    boxShadow: '0 4px 10px rgba(255, 138, 101, 0.25)'
                                  }}
                                >
                                  <i className="fas fa-ellipsis-v" style={{ fontSize: '12px' }}></i>
                                  Actions
                                  <i className="fas fa-chevron-down" style={{ fontSize: '10px' }}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                            <i className="fas fa-graduation-cap" style={{ fontSize: '48px', color: '#FF8A65', marginBottom: '16px' }}></i>
                            <h3>{internships.length === 0 ? 'No internships posted yet' : `No ${internshipFilter} internships`}</h3>
                            <p>{internships.length === 0 ? 'Start by posting your first internship to see it here!' : `You don't have any ${internshipFilter} internships at the moment.`}</p>
                            {internships.length === 0 && (
                              <button className="btn btn-primary btn-post-internship" onClick={handlePostInternshipClick} style={{ marginTop: '16px' }}>
                                <i className="fas fa-plus"></i> Post Your First Internship
                              </button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            </div>
          )}

          {/* Candidate Tracker Section */}
          {activeSection === 'candidate-tracker' && (
            <div className="page-section active" id="candidate-tracker" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              <RecruiterCandidateTracker
                onViewCandidate={handleViewCandidate}
                viewUserId={viewUserId}
                isMobileView={isMobileView}
                useCustomDropdown
              />
            </div>
          )}

          {/* Other sections */}
          {activeSection === 'messages' && (
            <div className="page-section active" style={{ marginLeft: '-20px', marginRight: '-20px', marginTop: '-1rem', paddingTop: '0' }}>
              <ProfessionalMessaging />
            </div>
          )}

          {activeSection === 'calendar' && (
            <div className="page-section active" style={{ display: 'flex', flexDirection: 'column', gap: '24px', marginTop: '-1rem', paddingTop: '0' }}>
              <div className="employer-calendar-layout" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <div className="employer-calendar-main" style={{ flex: '2 1 640px', minWidth: '640px', paddingRight: '20px' }}>
                  <InterviewCalendar
                    variant="employer"
                    onUpcomingChange={setUpcomingInterviews}
                    viewUserId={viewUserId}
                    isMobileView={isMobileView}
                    useCustomDropdown
                  />
                </div>
                <div className="employer-calendar-sidebar" style={{ flex: '1 1 360px', minWidth: '320px', marginTop: '160px' }}>
                  <UpcomingInterviewsPanel interviews={upcomingInterviews} />
                </div>
              </div>
            </div>
          )}

          {activeSection === 'analytics' && (
            <div className="page-section active" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              <AnalyticsDashboard isMobileView={isMobileView} useCustomDropdown />
            </div>
          )}

          {activeSection === 'settings' && (
            <div className="page-section active" style={{ marginTop: '-1rem', paddingTop: '0' }}>
              <RecruiterSettings user={user} onUpdateUser={handleUserUpdate} isMobileView={isMobileView} useCustomDropdown />
            </div>
          )}
        </div>
      </div>

      {/* Profile completion lock modal – requires at least 75% to use advanced features */}
      {showProfileLockModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => setShowProfileLockModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '480px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: '20px', color: '#f97316', fontSize: '40px' }}>
              <i className="fas fa-user-check" />
            </div>
            <h3 style={{ margin: '0 0 12px', fontSize: '20px', color: '#111' }}>
              Complete your profile to 75%
            </h3>
            <p style={{ margin: '0 0 24px', color: '#555', lineHeight: 1.5 }}>
              Please complete at least 75% of your employer profile before posting jobs,
              internships, or using advanced employer features. This helps jobseekers trust your brand.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                setShowProfileLockModal(false);
                navigate('/employer-my-profile');
              }}
              style={{ minWidth: '160px' }}
            >
              Go to My Profile
            </button>
          </div>
        </div>
      )}

      {/* Account under review modal – Post Job / Post Internship gated until Super Admin approval */}
      {showApprovalModal && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }} onClick={() => setShowApprovalModal(false)}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '440px',
            width: '90%',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            textAlign: 'center'
          }} onClick={e => e.stopPropagation()}>
            <div style={{ marginBottom: '20px', color: '#0d9488', fontSize: '48px' }}>
              <i className="fas fa-hourglass-half" />
            </div>
            <h3 style={{ margin: '0 0 12px', fontSize: '20px', color: '#111' }}>
              Account under review
            </h3>
            <p style={{ margin: '0 0 24px', color: '#555', lineHeight: 1.5 }}>
              Your account is under review and will be approved by the Super Admin. You will receive an email once your account is approved.
            </p>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowApprovalModal(false)}
              style={{ minWidth: '120px' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Job View/Edit Modal */}
      {showJobModal && selectedJob && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div className="modal-content edit-job-modal" style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '1400px',
            width: '98%',
            minWidth: '1200px',
            maxHeight: '90vh',
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
            margin: '0 auto',
            boxSizing: 'border-box'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ color: '#FF8A65', margin: 0 }}>
                {isEditing
                  ? (isSelectedInternship ? 'Edit Internship' : 'Edit Job')
                  : (isSelectedInternship ? 'Internship Details' : 'Job Details')}
              </h2>
              <button 
                onClick={handleCloseModal}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>
            
            {isEditing ? (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      {editJobData.title !== undefined ? 'Internship Title' : 'Job Title'}
                    </label>
                    <input
                      type="text"
                      value={editJobData.job_title || editJobData.title || ''}
                      onChange={(e) => setEditJobData({
                        ...editJobData, 
                        ...(editJobData.title !== undefined ? {title: e.target.value} : {job_title: e.target.value})
                      })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Company</label>
                    <input
                      type="text"
                      value={editJobData.company_name || editJobData.company || ''}
                      onChange={(e) => setEditJobData({
                        ...editJobData,
                        ...(editJobData.company !== undefined ? {company: e.target.value} : {company_name: e.target.value})
                      })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Job Type</label>
                    <LinkedInStyleSelect
                      value={editJobData.jobType || editJobData.job_type || 'Full-time'}
                      onChange={(val) => setEditJobData({...editJobData, jobType: val, job_type: val})}
                      options={jobTypes}
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Work Mode</label>
                    <LinkedInStyleSelect
                      value={editJobData.workMode || editJobData.work_mode || 'On-site'}
                      onChange={(val) => setEditJobData({...editJobData, workMode: val, work_mode: val})}
                      options={workModes}
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Experience Level</label>
                    <LinkedInStyleSelect
                      value={editJobData.experienceLevel || editJobData.experience_level || ''}
                      onChange={(val) => setEditJobData({...editJobData, experienceLevel: val, experience_level: val})}
                      options={experienceLevels}
                      placeholder="Select experience level"
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Number of Vacancies</label>
                    <input
                      type="number"
                      min="1"
                      value={editJobData.vacancies || 1}
                      onChange={(e) => setEditJobData({...editJobData, vacancies: parseInt(e.target.value) || 1})}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Industry</label>
                    <LinkedInStyleSelect
                      value={editJobData.industry || ''}
                      onChange={(val) => setEditJobData({...editJobData, industry: val})}
                      options={industries}
                      placeholder="Select industry"
                      searchable
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Department</label>
                    <LinkedInStyleSelect
                      value={editJobData.department || ''}
                      onChange={(val) => setEditJobData({...editJobData, department: val})}
                      options={departments}
                      placeholder="Select department"
                      searchable
                      containerClassName="w-full"
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Skills Required</label>
                    <LinkedInStyleSelect
                      value={editJobData.skills || []}
                      onChange={(val) => setEditJobData({...editJobData, skills: val})}
                      options={allSkills}
                      placeholder="Select skills or type custom skills"
                      searchable
                      multiple
                      allowCustom={true}
                      containerClassName="w-full"
                      openUpwards={true}
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Tools / Technologies</label>
                    <LinkedInStyleSelect
                      value={editJobData.tools || []}
                      onChange={(val) => setEditJobData({...editJobData, tools: val})}
                      options={allSkills}
                      placeholder="Select tools or type custom tools"
                      searchable
                      multiple
                      allowCustom={true}
                      containerClassName="w-full"
                      openUpwards={true}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Education Requirement</label>
                    <LinkedInStyleSelect
                      value={editJobData.education || 'Any'}
                      onChange={(val) => setEditJobData({...editJobData, education: val})}
                      options={educationLevels}
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>
                      {editJobData.stipend !== undefined ? 'Stipend' : 'Salary Type'}
                    </label>
                    <LinkedInStyleSelect
                      value={editJobData.salaryType || 'Negotiable'}
                      onChange={(val) => setEditJobData({...editJobData, salaryType: val})}
                      options={salaryTypes}
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Currency</label>
                    <LinkedInStyleSelect
                      value={editJobData.currency || 'USD'}
                      onChange={(val) => setEditJobData({...editJobData, currency: val})}
                      options={currencies}
                      searchable
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Salary Period</label>
                    <LinkedInStyleSelect
                      value={editJobData.salaryPeriod || 'yearly'}
                      onChange={(val) => setEditJobData({...editJobData, salaryPeriod: val})}
                      options={salaryPeriods}
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Minimum Salary</label>
                    <input
                      type="number"
                      value={editJobData.salaryMin || ''}
                      onChange={(e) => setEditJobData({...editJobData, salaryMin: e.target.value})}
                      placeholder="e.g., 50000"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Maximum Salary</label>
                    <input
                      type="number"
                      value={editJobData.salaryMax || ''}
                      onChange={(e) => setEditJobData({...editJobData, salaryMax: e.target.value})}
                      placeholder="e.g., 80000"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Benefits Offered</label>
                    <LinkedInStyleSelect
                      value={editJobData.benefits || []}
                      onChange={(val) => setEditJobData({...editJobData, benefits: val})}
                      options={benefits}
                      placeholder="Select benefits"
                      searchable
                      multiple
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Country</label>
                    <LinkedInStyleSelect
                      value={editJobData.country || ''}
                      onChange={(val) => setEditJobData({...editJobData, country: val})}
                      options={countries}
                      searchable
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>State / Province</label>
                    <input
                      type="text"
                      value={editJobData.state || ''}
                      onChange={(e) => setEditJobData({...editJobData, state: e.target.value})}
                      placeholder="e.g., Nairobi County"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>City</label>
                    <input
                      type="text"
                      value={editJobData.city || ''}
                      onChange={(e) => setEditJobData({...editJobData, city: e.target.value})}
                      placeholder="e.g., Nairobi"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Postal Code</label>
                    <input
                      type="text"
                      value={editJobData.postalCode || ''}
                      onChange={(e) => setEditJobData({...editJobData, postalCode: e.target.value})}
                      placeholder="e.g., 00100"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Office Address</label>
                    <input
                      type="text"
                      value={editJobData.officeAddress || ''}
                      onChange={(e) => setEditJobData({...editJobData, officeAddress: e.target.value})}
                      placeholder="Street address (for on-site or hybrid roles)"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Application Deadline</label>
                    <input
                      type="date"
                      value={editJobData.applicationDeadline || editJobData.application_deadline || ''}
                      onChange={(e) => setEditJobData({...editJobData, applicationDeadline: e.target.value, application_deadline: e.target.value})}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Preferred Joining Date</label>
                    <input
                      type="date"
                      value={editJobData.preferredJoiningDate || editJobData.preferred_joining_date || ''}
                      onChange={(e) => setEditJobData({...editJobData, preferredJoiningDate: e.target.value, preferred_joining_date: e.target.value})}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Application Method</label>
                    <LinkedInStyleSelect
                      value={editJobData.applyMethod || 'Apply through AksharJobs portal'}
                      onChange={(val) => setEditJobData({...editJobData, applyMethod: val})}
                      options={applyMethods}
                      containerClassName="w-full"
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Contact Email</label>
                    <input
                      type="email"
                      value={editJobData.contactEmail || editJobData.contact_email || ''}
                      onChange={(e) => setEditJobData({...editJobData, contactEmail: e.target.value, contact_email: e.target.value})}
                      placeholder="hr@company.com"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>HR / Recruiter Name</label>
                    <input
                      type="text"
                      value={editJobData.hrName || editJobData.hr_name || ''}
                      onChange={(e) => setEditJobData({...editJobData, hrName: e.target.value, hr_name: e.target.value})}
                      placeholder="e.g., John Doe"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Job Visibility</label>
                    <LinkedInStyleSelect
                      value={editJobData.visibility || 'public'}
                      onChange={(val) => setEditJobData({...editJobData, visibility: val})}
                      options={visibilityOptions}
                      containerClassName="w-full"
                    />
                  </div>
                  <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <input
                      id="edit-show-company-name"
                      type="checkbox"
                      checked={editJobData.showCompanyName !== false}
                      onChange={(e) => setEditJobData({...editJobData, showCompanyName: e.target.checked})}
                      style={{ marginTop: '4px' }}
                    />
                    <div>
                      <label htmlFor="edit-show-company-name" style={{ fontWeight: '500', cursor: 'pointer' }}>Show company name on public listing</label>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>Display your company name to candidates. Uncheck for confidential posting.</p>
                    </div>
                  </div>
                  <div style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <input
                      id="edit-show-hr-name"
                      type="checkbox"
                      checked={editJobData.showHrName !== false}
                      onChange={(e) => setEditJobData({...editJobData, showHrName: e.target.checked})}
                      style={{ marginTop: '4px' }}
                    />
                    <div>
                      <label htmlFor="edit-show-hr-name" style={{ fontWeight: '500', cursor: 'pointer' }}>Show employer / HR name to candidates</label>
                      <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#64748b' }}>Let applicants see the hiring manager name. Uncheck to keep private.</p>
                    </div>
                  </div>
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Job Summary / Description</label>
                  <textarea
                    value={editJobData.summary || editJobData.description || ''}
                    onChange={(e) => setEditJobData({...editJobData, summary: e.target.value, description: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' }}
                  />
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Key Responsibilities</label>
                  <textarea
                    value={editJobData.responsibilities || ''}
                    onChange={(e) => setEditJobData({...editJobData, responsibilities: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' }}
                  />
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Desired Candidate Profile</label>
                  <textarea
                    value={editJobData.desiredProfile || ''}
                    onChange={(e) => setEditJobData({...editJobData, desiredProfile: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' }}
                  />
                </div>
                
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: '500' }}>Company Overview</label>
                  <textarea
                    value={editJobData.companyOverview || ''}
                    onChange={(e) => setEditJobData({...editJobData, companyOverview: e.target.value})}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', minHeight: '100px' }}
                  />
                </div>
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCloseModal}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid #ddd',
                      background: 'white',
                      color: '#666',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveJob}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div>
                    <h3 style={{ color: '#FF8A65', marginBottom: '10px' }}>
                      {isSelectedInternship ? 'Internship Information' : 'Job Information'}
                    </h3>
                    <p><strong>Title:</strong> {selectedJob.job_title || selectedJob.title || 'N/A'}</p>
                    <p><strong>Company:</strong> {selectedJob.company_name || selectedJob.company || 'N/A'}</p>
                    <p><strong>Location:</strong> {selectedJob.location || 'N/A'}</p>
                    <p><strong>Type:</strong> {selectedJob.job_type || selectedJob.type || 'N/A'}</p>
                  </div>
                  <div>
                    <h3 style={{ color: '#FF8A65', marginBottom: '10px' }}>
                      {isSelectedInternship ? 'Internship Stats' : 'Job Stats'}
                    </h3>
                    <p><strong>Applications:</strong> {
                      Math.min(25, selectedJob.visibleApplications !== undefined 
                        ? selectedJob.visibleApplications
                        : (selectedJob.applicationCount || selectedJob.applicants?.length || 0))
                    }</p>
                    <p><strong>Views:</strong> {selectedJob.views || 0}</p>
                    <p><strong>Created:</strong> {selectedJob.created_at ? new Date(selectedJob.created_at).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleCloseModal}
                    style={{
                      padding: '10px 20px',
                      border: '2px solid #ddd',
                      background: 'white',
                      color: '#666',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleEditJob(selectedJob)}
                    style={{
                      padding: '10px 20px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #FF8A65 0%, #FF7043 100%)',
                      color: 'white',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    {isSelectedInternship ? 'Edit Internship' : 'Edit Job'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    {(openInternshipMenu && activeInternshipForMenu && internshipMenuPosition) && (
      <div
        className="employer-action-menu-portal"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: internshipMenuPosition.top,
          left: internshipMenuPosition.left,
          width: internshipMenuPosition.width,
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid rgba(148, 163, 184, 0.25)',
          boxShadow: '0 16px 30px rgba(15, 23, 42, 0.15)',
          padding: '6px 0',
          zIndex: 5000
        }}
      >
        <button
          type="button"
          className="employer-action-menu-item btn-custom-size"
          onClick={() => handleInternshipMenuAction('view')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-eye" style={{ color: '#FF8A65' }}></i>
          View
        </button>
        <button
          type="button"
          className="employer-action-menu-item btn-custom-size"
          onClick={() => handleInternshipMenuAction('edit')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-edit" style={{ color: '#14b8a6' }}></i>
          Edit
        </button>
      </div>
    )}
    {(openJobMenu && activeJobForMenu && jobMenuPosition) && (
      <div
        className="employer-action-menu-portal"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top: jobMenuPosition.top,
          left: jobMenuPosition.left,
          width: jobMenuPosition.width,
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid rgba(148, 163, 184, 0.25)',
          boxShadow: '0 16px 30px rgba(15, 23, 42, 0.15)',
          padding: '6px 0',
          zIndex: 5000
        }}
      >
        <button
          type="button"
          className="employer-action-menu-item btn-custom-size"
          onClick={() => handleJobMenuAction('view')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-eye" style={{ color: '#FF8A65' }}></i>
          View
        </button>
        <button
          type="button"
          className="employer-action-menu-item btn-custom-size"
          onClick={() => handleJobMenuAction('edit')}
          style={{
            width: '100%',
            padding: '8px 16px',
            background: 'none',
            border: 'none',
            textAlign: 'left',
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '13px',
            color: '#1f2937',
            cursor: 'pointer'
          }}
        >
          <i className="fas fa-edit" style={{ color: '#14b8a6' }}></i>
          Edit
        </button>
      </div>
    )}
      {isMobileView && (
        <button
          type="button"
          className="em-dashboard-fab btn-custom-size"
          aria-label={showMobileSidebar ? 'Close menu' : 'Open navigation menu'}
          onClick={() => setShowMobileSidebar(!showMobileSidebar)}
        >
          <FontAwesomeIcon icon={showMobileSidebar ? faTimes : faBars} />
        </button>
      )}
    </>
  );
};

export default EmployerDashboard;